import { useEffect } from "react";
import type { URLSearchParamsInit } from "react-router-dom";
import {
  libraryItemStatusOptions,
  librarySourceTypeOptions,
  type LibraryItemStatus,
  type LibrarySourceType,
} from "@/lib/library";
import type { LibrarySortOption } from "@/components/library/myLibraryHelpers";

type Args = {
  searchParams: URLSearchParams;
  setLibrarySearchQuery: (updater: (cur: string) => string) => void;
  setSortBy: (updater: (cur: LibrarySortOption) => LibrarySortOption) => void;
  setSelectedCategoryIds: (updater: (cur: string[]) => string[]) => void;
  setSelectedStatusFilters: (updater: (cur: LibraryItemStatus[]) => LibraryItemStatus[]) => void;
  setSelectedSourceTypes: (updater: (cur: LibrarySourceType[]) => LibrarySourceType[]) => void;
};

/**
 * Sync library filter / search / sort state from URL search params.
 * Mirrors the previous inline effect in MyLibrary.tsx; pulled out so the
 * page component stays focused on orchestration.
 */
export function useMyLibraryUrlSync({
  searchParams,
  setLibrarySearchQuery,
  setSortBy,
  setSelectedCategoryIds,
  setSelectedStatusFilters,
  setSelectedSourceTypes,
}: Args) {
  useEffect(() => {
    const nextSearch = searchParams.get("q") ?? "";
    const nextSort = searchParams.get("sort");
    const nextCategories = (searchParams.get("categories") ?? "").split(",").filter(Boolean);
    const nextStatuses = (searchParams.get("status") ?? "")
      .split(",")
      .filter((value): value is LibraryItemStatus =>
        libraryItemStatusOptions.some((option) => option.value === value),
      );
    const nextSourceTypes = (searchParams.get("type") ?? "")
      .split(",")
      .filter((value): value is LibrarySourceType =>
        librarySourceTypeOptions.some((option) => option.value === value),
      );

    setLibrarySearchQuery((current) => (current === nextSearch ? current : nextSearch));
    if (nextSort && ["recent", "title", "author", "published", "status"].includes(nextSort)) {
      setSortBy((current) => (current === nextSort ? current : (nextSort as LibrarySortOption)));
    }
    if (nextCategories.length) {
      setSelectedCategoryIds((current) =>
        current.join(",") === nextCategories.join(",") ? current : nextCategories,
      );
    }
    if (nextStatuses.length) {
      setSelectedStatusFilters((current) =>
        current.join(",") === nextStatuses.join(",") ? current : nextStatuses,
      );
    }
    setSelectedSourceTypes((current) =>
      current.join(",") === nextSourceTypes.join(",") ? current : nextSourceTypes,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
}

// Re-export for type compat
export type { URLSearchParamsInit };
